import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  ButtonBase,
  Container,
  Fade,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { motion } from 'framer-motion';
import {
  LayoutRenderer,
  renderDoc,
  docPath,
  Sidebar,
  MobileNav,
  PreviewHeader,
} from '@client/components';

const MotionButtonBase = motion(ButtonBase);

const GlassyActionRow = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <MotionButtonBase
      onClick={onClick}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        p: 3,
        mt: 5,
        borderRadius: '0.75rem',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 40px -10px rgba(102, 126, 234, 0.3)',
        },
      }}
    >
      <Box>
        <Typography
          variant="button"
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            letterSpacing: '0.5px',
          }}
        >
          Next Section
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: 500,
            mt: 0.5,
            fontSize: '0.9rem',
          }}
        >
          {label}
        </Typography>
      </Box>

      <motion.div
        animate={{ x: isHovered ? 8 : 0, scale: isHovered ? 1.1 : 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <ChevronRightIcon
          sx={{
            color: '#667eea',
            fontSize: 28,
            transition: 'color 0.3s ease',
          }}
        />
      </motion.div>
    </MotionButtonBase>
  );
};

export function DocPage() {
  const { doc_name } = useParams();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isValidSlug = docPath.pages.some((page) => page.slug === doc_name);
  const slug = isValidSlug ? doc_name : docPath.default;

  const { meta, content } = renderDoc(slug);

  const handleNext = () => {
    const currentIndex = docPath.pages.findIndex((page) => page.slug === slug);
    const nextIndex = (currentIndex + 1) % docPath.pages.length;
    const nextSlug = docPath.pages[nextIndex].slug;
    navigate(`/docs/${nextSlug}`);
  };

  const currentIndex = docPath.pages.findIndex((page) => page.slug === slug);
  const nextIndex = (currentIndex + 1) % docPath.pages.length;
  const nextPageTitle = docPath.pages[nextIndex].title;

  const progress = ((currentIndex + 1) / docPath.pages.length) * 100;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
      }}
    >
      <PreviewHeader />

      {meta.banner && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: '200px', sm: '300px', md: '350px' },
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to top, #0f0f1a, transparent)',
            },
          }}
        >
          <Box
            component="img"
            src={meta.banner}
            alt={`${meta.title} Banner`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: { xs: 16, md: 80 },
              zIndex: 2,
            }}
          >
            <Chip
              icon={<MenuBookIcon />}
              label={`${currentIndex + 1} of ${docPath.pages.length}`}
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                color: '#fff',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                backdropFilter: 'blur(10px)',
              }}
            />
          </Box>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ display: 'flex', flex: 1 }}>
        {!isMobile && <Sidebar />}
        {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            maxWidth: '900px',
            width: '100%',
            mx: 'auto',
            mb: { xs: 8, md: 4 },
          }}
        >
          <Fade in timeout={600}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              }}
            >
              <Breadcrumbs
                separator={<ChevronRightIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}
              >
                <Link
                  component="button"
                  onClick={() => navigate('/')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { color: '#667eea' },
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Home
                </Link>
                <Typography color="text.primary">Documentation</Typography>
              </Breadcrumbs>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#667eea',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    mb: 1,
                    display: 'block',
                  }}
                >
                  Documentation
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    mb: 2,
                    background: 'linear-gradient(135deg, #fff 0%, #a0a0a0 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {meta.title}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                  }}
                >
                  {meta.description}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '2px',
                  mb: 4,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </Box>

              <LayoutRenderer meta={meta} content={content} />

              <GlassyActionRow label={nextPageTitle} onClick={handleNext} />
            </Paper>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
}